import { Component, OnInit } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute } from '@angular/router'
import { NuclearObservable } from '../nuclear-observable'
import { BaseComponent } from '../components/base.component'
import { CustomerActions } from '../actions/customer-actions'
import { KeywordActions } from '../actions/keyword-actions'
import { customerGetters } from '../stores/customer-store'
import { Customer, Keyword, Writer, Alert } from '../types'
import { newProposedKeywordsAlert } from '../logic/customer-alerts'

@Component({
  selector: 'proposed-topics-page',
  template: `
    <div class="container-fluid">
      <div class="row top-buffer">
        <div class="col-sm-6">
          <h4>
            <div>
              Proposed Topics
              <span class="corner-alert alert-sm fa-stack" *ngIf="newProposedKeywordsAlert()" [innerHTML]="newProposedKeywordsAlert()?.render"></span>
            </div>
          </h4>
          <p>Accept a topic today, activate the request at your leisure.</p>
          <p *ngIf="proposedKeywords.length === 0">
            You currently have no proposed topics from preferred writers.
            <br/>
            <br/>
            Prefer more writers to build a reliable group that reflects the voice of your business.
          </p>
          <div *ngFor="let keyword of proposedKeywords" class="mb-3">
            <proposed-topic-box [keyword]="keyword"
                                [writer]="writer(keyword)"
                                [customerId]="customer?.id"
                                *ngIf="writer(keyword)">
            </proposed-topic-box>
          </div>
        </div>
        <div class="col-sm-6">
          <h4>Accepted Topics</h4>
          <p>Visit your Inactive Requests to activate accepted topics.</p>
          <p *ngIf="acceptedKeywords.length === 0">You haven't accepted any proposed topics yet.</p>
          <div *ngFor="let keyword of acceptedKeywords" class="mb-3">
            <proposed-topic-box [keyword]="keyword"
                                [writer]="writer(keyword)"
                                [customerId]="customer?.id"
                                *ngIf="writer(keyword)">
            </proposed-topic-box>
          </div>
        </div>
      </div>
    </div>
 `
})
export class ProposedTopicsPage extends BaseComponent implements OnInit {
  customer: Customer;
  private keywords: Keyword[] = [];
  private writers: Writer[] = [];

  constructor(private route: ActivatedRoute,
    private customerActions: CustomerActions,
    private keywordActions: KeywordActions) {
    super()
  }

  ngOnInit(): void {
    const titleService = new Title({})
    titleService.setTitle('Proposed Topics')

    const routeObserver = this.route.parent.params.subscribe(params => {
      this.clearRouteObservers()
      const customerId = +params['customerId']

      this.customerActions.loadCustomer(customerId)
      this.keywordActions.loadKeywordsWithWriters(customerId, 'from_writer')

      const custObserver = NuclearObservable.from<Customer>(customerGetters.customer(customerId), { toJS: true }).subscribe(c => {
        this.customer = c
        if (this.customer) {
          titleService.setTitle('Proposed Topics - ' + this.customer.businessName)
        }
      })
      const kwObserver = NuclearObservable.from<Keyword[]>(customerGetters.keywords(customerId), { toJS: true }).subscribe(ks => this.keywords = ks)

      this.routeObservers.push(custObserver, kwObserver)
    })

    const writerObserver = NuclearObservable.from<Writer[]>(customerGetters.writers, { toJS: true }).subscribe(ws => this.writers = ws)

    this.observers.push(routeObserver, writerObserver)
  }

  get proposedAndAcceptedKeywords(): Keyword[] {
    return this.keywords.filter(k => !!k.proposedAt)
  }

  get proposedKeywords(): Keyword[] {
    return this.proposedAndAcceptedKeywords.filter(k => k.state === 'proposed')
  }

  get acceptedKeywords(): Keyword[] {
    return this.proposedAndAcceptedKeywords.filter(k => !!k.acceptedAt)
  }

  // if the keyword is re-assigned, then the previous writer is the proposer
  writer(k: Keyword): Writer {
    return this.writers.find(w => w.id === k.previousWriterId || w.id === k.writerId)
  }

  newProposedKeywordsAlert(): Alert {
    return this.customer && newProposedKeywordsAlert(this.customer)
  }
}
